import React, { FC, useState } from 'react';
import styles from './ToolsCard.module.css';
import { Alert, Card, CardActions, CardContent, Snackbar, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShareIcon from '@mui/icons-material/Share';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import StraightenIcon from '@mui/icons-material/Straighten';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { assert } from '../../assert';
import { formatFrameUrl } from '../../FormatFrameUrl';
import { activeVideoActions } from '../../state-slices/ActiveVideo';
import { screenshotActions } from '../../state-slices/screenshot-dialog/Screenshot';

interface ToolsCardProps {}

// Debug/verification aid: used to confirm which bundle/version is currently running in the browser.
// If you change this string and don't see it in the UI, you're likely not viewing the expected dev server.
const TOOLS_BUILD_STAMP = 'TOOLS_BUILD=2026-03-10-ruler-toggle';

const ToolsCard: FC<ToolsCardProps> = () => {
    const currentProjectSafeName = useAppSelector(state => state.activeVideoSlice.projectState.currentProject?.safeName);
    const currentProject = useAppSelector(state => state.activeVideoSlice.projectState.currentProject);
    const currentRunSafeName = useAppSelector(state => state.activeVideoSlice.projectState.selectedRun?.safeName);
    const currentFrameUrlTemplate = useAppSelector(state => state.activeVideoSlice.projectState.selectedClip?.fullFrameTemplateHref);
    const isPlaying = useAppSelector(state => state.activeVideoSlice.videoIsPlaying);
    const currentClipName = useAppSelector(state => state.activeVideoSlice.projectState.selectedClip?.name);
    const currentFrame = useAppSelector(state => state.activeVideoSlice.currentFrame);

    const showingGrid = useAppSelector(state => state.activeVideoSlice.shouldShowGrid);
    const showingRuler = useAppSelector(state => state.activeVideoSlice.shouldShowRuler);
    const gridButtonEnabled = useAppSelector(state => state.activeVideoSlice.buttonStates.shouldShowGridButton);

    const dispatch = useAppDispatch();
    const [openLinkCopiedSnackbar, setOpenLinkCopiedSnackbar] = useState(false);

    function createShareLink() {
        const linkToCurrentTime = `${window.location.origin}/projects/play/${currentProjectSafeName}/${currentRunSafeName}/${currentClipName}/${currentFrame}`;
        window.navigator.clipboard.writeText(linkToCurrentTime);
        setOpenLinkCopiedSnackbar(true);
    }

    function copySnackbarClosed() {
        setOpenLinkCopiedSnackbar(false);
    }

    function toggleGrid() {
        dispatch(activeVideoActions.toggleGrid());
    }

    function toggleRuler() {
        dispatch(activeVideoActions.toggleRuler());
    }

    function openZoomDialog() {
        assert(currentProject);
        assert(currentFrameUrlTemplate);
        const screenshotImage = formatFrameUrl(currentFrame, currentFrameUrlTemplate, currentProject);
        dispatch(screenshotActions.openScreenshotDialog(screenshotImage));
    }

    return (
        <Card>
            <CardContent>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'none', marginTop: '-6px', marginBottom: '6px' }}
                >
                    {TOOLS_BUILD_STAMP}
                </Typography>

                <div className={styles.ToolsList}>
                    <Button
                        variant="text"
                        data-id="zoom-button"
                        onClick={() => openZoomDialog()}
                        disabled={isPlaying}
                        startIcon={<ZoomInIcon />}
                    >
                        Zoom
                    </Button>

                    {gridButtonEnabled && (
                        <Button
                            variant="text"
                            data-id="show-hide-grid"
                            onClick={() => toggleGrid()}
                            startIcon={<FilterListIcon />}
                        >
                            Grid {showingGrid ? 'Off' : 'On'}
                        </Button>
                    )}

                    <Button
                        variant="text"
                        data-id="show-hide-ruler"
                        onClick={() => toggleRuler()}
                        startIcon={<StraightenIcon />}
                    >
                        Ruler {showingRuler ? 'Off' : 'On'}
                    </Button>
                    <Button variant="text" onClick={createShareLink} data-id="share-link" startIcon={<ShareIcon />}>
                        Copy link
                    </Button>
                </div>
            </CardContent>
            <CardActions></CardActions>

            <Snackbar
                open={openLinkCopiedSnackbar}
                autoHideDuration={6000}
                anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                onClose={copySnackbarClosed}
            >
                <Alert data-id="link-copied-alert" onClose={copySnackbarClosed} severity="success" sx={{ width: '100%' }}>
                    A link to this video and time has been copied to clipboard.
                </Alert>
            </Snackbar>
        </Card>
    );
};

export default ToolsCard;
